import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import "./Coupons.css";
import {
  createCoupon,
  getCouponRedemptions,
  getCoupons,
  revokeRedemption,
  toggleCouponStatus,
} from "../../api/lib/coupons";

const EMPTY_FORM = {
  label: "",
  code: "",
  grantDays: "30",
  maxSeats: "",
  validFrom: "",
  validUntil: "",
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";

// Expiry is not stored, so a lapsed redemption still reads GRANTED in the database.
const displayState = (redemption) => {
  if (redemption.grantState !== "GRANTED") return redemption.grantState;
  return new Date(redemption.grantExpiresAt) < new Date() ? "EXPIRED" : "GRANTED";
};

// validUntil is when the code stops being typeable; grantExpiresAt is when someone's access
// ends. Labelled in full on screen because the two get confused.
const Coupons = ({ searchQuery }) => {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState("");
  const [redemptions, setRedemptions] = useState([]);

  const loadCoupons = async () => {
    try {
      const { data } = await getCoupons();
      setCoupons(data?.data ?? []);
    } catch (error) {
      toast.error("Could not load coupons");
    }
  };

  useEffect(() => {
    loadCoupons();
  }, []);

  const setField = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = { label: form.label.trim(), grantDays: Number(form.grantDays) };
      if (form.code.trim()) payload.code = form.code.trim();
      if (form.maxSeats) payload.maxSeats = Number(form.maxSeats);
      if (form.validFrom) payload.validFrom = form.validFrom;
      if (form.validUntil) payload.validUntil = form.validUntil;

      const { data } = await createCoupon(payload);
      toast.success(`Code ${data?.data?.code} created`);
      setForm(EMPTY_FORM);
      setShowForm(false);
      loadCoupons();
    } catch (error) {
      toast.error(error?.response?.data?.message ?? "Could not create the coupon");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (couponId) => {
    try {
      await toggleCouponStatus(couponId);
      loadCoupons();
    } catch (error) {
      toast.error("Could not change the coupon status");
    }
  };

  const openRedemptions = async (couponId) => {
    if (expandedId === couponId) {
      setExpandedId("");
      return;
    }
    try {
      const { data } = await getCouponRedemptions(couponId);
      setRedemptions(data?.data ?? []);
      setExpandedId(couponId);
    } catch (error) {
      toast.error("Could not load who used this code");
    }
  };

  // Does not free the seat, so the used/max count deliberately stays put.
  const revoke = async (redemptionId, couponId) => {
    try {
      await revokeRedemption(redemptionId);
      toast.success("Access revoked");
      const { data } = await getCouponRedemptions(couponId);
      setRedemptions(data?.data ?? []);
    } catch (error) {
      toast.error(error?.response?.data?.message ?? "Could not revoke access");
    }
  };

  const query = (searchQuery ?? "").trim().toLowerCase();
  const visible = query
    ? coupons.filter(
        (c) => c.label?.toLowerCase().includes(query) || c.code?.toLowerCase().includes(query)
      )
    : coupons;

  return (
    <div className="coupon-window">
      <div className="coupon-head">
        <p className="tot-results">Showing {visible.length} coupons</p>
        <button className="coupon-add-btn" onClick={() => setShowForm((prev) => !prev)}>
          {showForm ? "Cancel" : "Add"}
        </button>
      </div>

      {showForm && (
        <form className="coupon-form" onSubmit={submit}>
          <label className="coupon-field">
            <span>Who is it for</span>
            <input value={form.label} onChange={setField("label")} placeholder="Highway Daycare" required />
          </label>
          <label className="coupon-field">
            <span>Code</span>
            <input value={form.code} onChange={setField("code")} placeholder="Leave blank to generate" />
          </label>
          <label className="coupon-field">
            <span>Days of access</span>
            <input type="number" min="1" max="730" value={form.grantDays} onChange={setField("grantDays")} required />
          </label>
          <label className="coupon-field">
            <span>How many people</span>
            <input type="number" min="1" value={form.maxSeats} onChange={setField("maxSeats")} placeholder="Unlimited" />
          </label>
          <label className="coupon-field">
            <span>Code works from</span>
            <input type="date" value={form.validFrom} onChange={setField("validFrom")} />
          </label>
          <label className="coupon-field">
            <span>Code stops working</span>
            <input type="date" value={form.validUntil} onChange={setField("validUntil")} />
          </label>
          <button className="coupon-save-btn" type="submit" disabled={saving}>
            {saving ? "Creating…" : "Create coupon"}
          </button>
        </form>
      )}

      <div className="coupon-tab">
        <div className="coupon-row coupon-row-head">
          <p>Code</p>
          <p>Who is it for</p>
          <p>Access</p>
          <p>Used</p>
          <p>Code stops working</p>
          <p>Status</p>
          <p>Who used it</p>
        </div>
        <hr className="coupon-tab-hr" />

        {visible.map((coupon) => (
          <div key={coupon._id}>
            <div className="coupon-row">
              <p className="coupon-code">{coupon.code}</p>
              <p>{coupon.label}</p>
              <p>{coupon.grantDays} days</p>
              <p>
                {coupon.seatsUsed}
                {coupon.maxSeats ? ` / ${coupon.maxSeats}` : " / ∞"}
              </p>
              <p>{formatDate(coupon.validUntil)}</p>
              <p>
                <button
                  className={coupon.isActive ? "coupon-pill coupon-pill-on" : "coupon-pill coupon-pill-off"}
                  onClick={() => toggle(coupon._id)}
                  title="Stops the code being redeemed. Access already given is not affected."
                >
                  {coupon.isActive ? "Active" : "Off"}
                </button>
              </p>
              <p>
                <button className="coupon-link" onClick={() => openRedemptions(coupon._id)}>
                  {expandedId === coupon._id ? "Hide" : "View"}
                </button>
              </p>
            </div>

            {expandedId === coupon._id && (
              <div className="coupon-redemptions">
                {redemptions.length === 0 && <p className="coupon-empty">Nobody has used this code yet.</p>}
                {redemptions.map((redemption) => (
                  <div className="coupon-redemption" key={redemption._id}>
                    <p>{redemption.userId?.name || redemption.userId?.email || "Deleted user"}</p>
                    <p>{redemption.userId?.email ?? ""}</p>
                    <p>Access until {formatDate(redemption.grantExpiresAt)}</p>
                    <p>{displayState(redemption)}</p>
                    <p>
                      {displayState(redemption) === "GRANTED" && (
                        <button className="coupon-revoke" onClick={() => revoke(redemption._id, coupon._id)}>
                          Revoke
                        </button>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {visible.length === 0 && <p className="coupon-empty">No coupons yet.</p>}
      </div>
    </div>
  );
};

export default Coupons;
